import { Button } from "primereact/button";
import Reorder from "components/fragments/dashboard/components/tab/reorder/Reorder";
import { ReorderType } from "components/fragments/dashboard/components/tab/reorder/DraggableItem";
import { T } from "services/lang/LanguageService";
import { confirmDialog, ConfirmDialog } from "primereact/confirmdialog";
import React, { useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";
import EditColumn from "./EditColumn";
import {
  CellType,
  ColumnOptions,
  IUniversalDatasetTableColumn,
  IUniversalDatasetTableConfig,
  RemoveContentType,
} from "../UniversalDatasetTableTypes";
import "./UniversalDatasetTableEditor.scss";

const newColumnTitle = "New Column";

interface RowData {
  columns: IUniversalDatasetTableColumn[];
  rowIndex: number;
  isSeparator?: boolean;
}

interface CellTypesEditorProps {
  currentConfig: IUniversalDatasetTableConfig;
  setCurrentConfig: (config: IUniversalDatasetTableConfig) => void;
}

const CellTypesEditor = ({ currentConfig, setCurrentConfig }: CellTypesEditorProps) => {
  const [isEditColumnDialogVisible, setEditColumnDialogVisible] = useState<boolean>(false);
  const [editingColumnIndex, setEditingColumnIndex] = useState<number | null>(null);
  const [isColumnsReorderDialogVisible, setIsColumnsReorderDialogVisible] = useState<boolean>(false);

  const openEditColumnDialog = (index: number) => {
    setEditingColumnIndex(index);
    setEditColumnDialogVisible(true);
  };

  const addRow = () => {
    const isFirstRow = currentConfig.rows.length === 0;
    const newRow = {
      uuid: uuidv4(),
      columns:
        currentConfig.rows[currentConfig.rows.length - 1]?.columns.map(column => ({
          cell: {
            type: column.cell.type,
            displayDatasetValueTime: column.cell.displayDatasetValueTime,
            setMinMaxColors: column.cell.setMinMaxColors,
            minMaxColor: column.cell.minMaxColor,
            minMaxValue: column.cell.minMaxValue,
            timePattern: column.cell.timePattern,
            highlightColor: column.cell.highlightColor,
          },
        })) || [],
    };

    const newConfig = {
      ...currentConfig,
      rows: [...currentConfig.rows, newRow],
    };

    if (isFirstRow) {
      newConfig.columnsOptions = [
        ...currentConfig.columnsOptions,
        {
          id: uuidv4(),
          title: newColumnTitle,
          columnHeaderVisibility: true,
          columnVisibility: true,
        },
      ];

      newConfig.rows = newConfig.rows.map(row => ({
        ...row,
        columns: [...row.columns, { cell: { type: CellType.DATASET } }],
      }));
    }

    setCurrentConfig(newConfig);
  };

  const addColumn = () => {
    const newConfig = {
      ...currentConfig,
      columnsOptions: [
        ...currentConfig.columnsOptions,
        {
          id: uuidv4(),
          title: newColumnTitle,
          columnHeaderVisibility: true,
          columnVisibility: true,
        },
      ],
      rows: currentConfig.rows.map(row => ({
        ...row,
        columns: [...row.columns, { cell: { type: CellType.DATASET } }],
      })),
    };
    setCurrentConfig(newConfig);
  };

  const addSeparator = () => {
    const newConfig = {
      ...currentConfig,
      rows: [
        ...currentConfig.rows,
        {
          uuid: uuidv4(),
          columns:
            currentConfig.rows[0]?.columns.map(() => ({
              cell: { type: CellType.SEPARATOR },
            })) || [],
          isSeparator: true,
        },
      ],
    };
    setCurrentConfig(newConfig);
  };

  const removeRow = (rowIndex: number) => {
    confirmDialog({
      message: T("confirm-dialog.are-you-sure-you-want-to-remove-this-row"),
      header: T("confirm-dialog.confirmation"),
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      acceptLabel: T("action.remove"),
      rejectLabel: T("word.no"),
      accept: () => {
        const newConfig = {
          ...currentConfig,
          rows: currentConfig.rows.toSpliced(rowIndex, 1),
          columnsOptions: currentConfig.rows.length === 1 ? [] : currentConfig.columnsOptions,
        };
        setCurrentConfig(newConfig);
      },
    });
  };

  const removeColumn = (colIndex: number) => {
    const newConfig = {
      ...currentConfig,
      columnsOptions: currentConfig.columnsOptions.toSpliced(colIndex, 1),
      rows:
        currentConfig.columnsOptions.length === 1
          ? []
          : currentConfig.rows.map(row => ({
              ...row,
              columns: row.columns.toSpliced(colIndex, 1),
            })),
    };
    setCurrentConfig(newConfig);
  };

  const updateCellType = (rowIndex: number, colIndex: number, type: CellType) => {
    const newConfig = {
      ...currentConfig,
      rows: currentConfig.rows.map((row, index) => {
        if (index === rowIndex) {
          return {
            ...row,
            columns: row.columns.map((column, columnIndex) => {
              if (columnIndex === colIndex) {
                return {
                  ...column,
                  cell: {
                    ...column.cell,
                    type,
                    value: undefined,
                    displayDatasetValueTime: undefined,
                    timePattern: undefined,
                  },
                };
              }
              return column;
            }),
          };
        }
        return row;
      }),
    };
    setCurrentConfig(newConfig);
  };

  const handleUpdateColumnOptions = (newColumnOptions: ColumnOptions) => {
    const newConfig = {
      ...currentConfig,
      columnsOptions: currentConfig.columnsOptions.map((header, index) => {
        if (index === editingColumnIndex) {
          return newColumnOptions;
        }
        return header;
      }),
    };
    setCurrentConfig(newConfig);
  };

  const removeButtonTemplate = (index: number, removeContentType: RemoveContentType) => {
    return (
      <Button
        icon="pi pi-times"
        className="p-button-text p-button-danger"
        tooltip={`${removeContentType === RemoveContentType.COLUMN ? T("action.remove-column") : T("action.remove-row")}`}
        tooltipOptions={{ position: "bottom" }}
        onClick={() =>
          removeContentType === RemoveContentType.COLUMN ? removeColumn(index) : removeRow(index)
        }
      />
    );
  };

  const renderColumnHeader = (columnIndex: number) => {
    return (
      <div className="flex justify-content-between align-items-center">
        <div>{currentConfig.columnsOptions[columnIndex]?.title || newColumnTitle}</div>
        <i
          className="pi pi-pencil cursor-pointer px-1 p-button p-button-text"
          onClick={() => openEditColumnDialog(columnIndex)}
          data-pr-tooltip={T("action.edit-column")}
        />
      </div>
    );
  };

  const renderTypesCell = (rowData: RowData, rowIndex: number, columnIndex: number) => {
    if (rowData.isSeparator) {
      return <span className="font-bold" />;
    }

    const value = rowData.columns[columnIndex]?.cell.type;

    return (
      <select value={value} onChange={e => updateCellType(rowIndex, columnIndex, e.target.value as CellType)}>
        {Object.values(CellType)
          .filter(type => type !== CellType.SEPARATOR)
          .map(type => (
            <option key={type} value={type}>
              {type}
            </option>
          ))}
      </select>
    );
  };

  const onDragEnd = (result: any) => {
    if (!result.destination) return;

    const { source, destination } = result;

    const orderedValues = [...currentConfig.rows];
    const [removed] = orderedValues.splice(source.index, 1);
    orderedValues.splice(destination.index, 0, removed);

    setCurrentConfig({
      ...currentConfig,
      rows: orderedValues,
    });
  };

  return (
    <div className="universal-table-cell-types-editor">
      <ConfirmDialog />
      <div className="flex flex-column gap-2">
        <div className="flex flex-row justify-content-between">
          <div className="flex gap-2">
            <Button
              label={T("action.add-row")}
              icon="pi pi-plus"
              className="component-editor-button icon-green"
              onClick={addRow}
            />
            <Button
              label={T("action.add-column")}
              icon="pi pi-plus"
              className="component-editor-button icon-green"
              onClick={addColumn}
              disabled={currentConfig.rows.length === 0}
            />
            <Button
              label={T("action.add-separator")}
              icon="pi pi-plus"
              className="component-editor-button icon-green"
              onClick={addSeparator}
            />
          </div>
          <Button
            label={T("label.reorder-columns")}
            icon="pi pi-arrow-right-arrow-left"
            className="component-editor-button"
            onClick={() => setIsColumnsReorderDialogVisible(true)}
          />
        </div>
        <table>
          <thead>
            <tr>
              <th />
              {currentConfig.columnsOptions.map((col, colIdx) => (
                <th key={col.id} className="types-editor-header-cell">
                  {renderColumnHeader(colIdx)}
                </th>
              ))}
            </tr>
          </thead>
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="table-rows">
              {provided => (
                <tbody ref={provided.innerRef} {...provided.droppableProps}>
                  {currentConfig.rows.map((row, rowIdx) => (
                    <Draggable key={row.uuid} draggableId={row.uuid ?? ""} index={rowIdx}>
                      {(currentProvided, snapshot) => (
                        <tr
                          ref={currentProvided.innerRef}
                          {...currentProvided.draggableProps}
                          {...currentProvided.dragHandleProps}
                          style={{
                            ...currentProvided.draggableProps.style,
                            background: snapshot.isDragging ? "#f0f0f0" : undefined,
                          }}
                        >
                          <td>
                            <i className="pi pi-bars cursor-move" />
                          </td>
                          {row.columns.map((col, colIdx) => (
                            <td key={colIdx}>{renderTypesCell(row as RowData, rowIdx, colIdx)}</td>
                          ))}
                          <td>{removeButtonTemplate(rowIdx, RemoveContentType.ROW)}</td>
                        </tr>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </tbody>
              )}
            </Droppable>
          </DragDropContext>
        </table>
      </div>
      {isEditColumnDialogVisible && editingColumnIndex != null && (
        <EditColumn
          selectedColumnOptions={currentConfig.columnsOptions[editingColumnIndex]}
          setSelectedColumnOptions={handleUpdateColumnOptions}
          isEditColumnDialogVisible={isEditColumnDialogVisible}
          setEditColumnDialogVisible={setEditColumnDialogVisible}
          removeColumn={() => removeColumn(editingColumnIndex)}
        />
      )}
      {isColumnsReorderDialogVisible && (
        <Reorder
          setReorderDialogVisibility={setIsColumnsReorderDialogVisible}
          universalTableConfig={currentConfig}
          reorderDialogVisibility={isColumnsReorderDialogVisible}
          reorderType={ReorderType.UNIVERSAL_TABLE_COLUMNS}
          onUniversalTableConfigChange={setCurrentConfig}
        />
      )}
    </div>
  );
};

export default CellTypesEditor;
